import {
  AuthenticateParameters,
  AuthTokenRequest,
  ICareerHubAppconfig,
  UserProfile,
  UserRequest,
} from '../../public/careerHub.types';
import * as Msal from 'msal';
import { AuthenticationService } from '../services/AuthenticationService';
import { IAuthenticationToken, IAuthenticationUser } from '../services/interfaces/IAuthenticationService';

/**
 * Namespace to interact with the authentication-specific part of the SDK.
 * This object is used for starting or completing authentication flows.
 */
export namespace authentication {
  let authService: AuthenticationService;
  let appconfig: ICareerHubAppconfig;
  let notifyInitializer: () => void;

  export function initialize(config: ICareerHubAppconfig, callback?: () => void): void {
    appconfig = config;
    const msal_config: Msal.Configuration = {
      auth: {
        clientId: appconfig.clientId,
        authority: `https://login.microsoftonline.com/${appconfig.tenant || 'common'}`,
        navigateToLoginRequestUrl: false,
        redirectUri: appconfig.redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage', // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
      },
    };

    authService = new AuthenticationService(msal_config);
    if (callback) {
      notifyInitializer = callback;
      callback();
    }
  }

  export function logout(): void {
    return authService.signOut();
  }

  /**
   * Initiates an authentication request, which opens a new window with the specified settings.
   */
  export function authenticate(authenticateParameters?: AuthenticateParameters): void {
    const account = authService.login();
    if (account) {
      notifyInitializer && notifyInitializer();
      authenticateParameters?.successCallback && authenticateParameters?.successCallback(account.userName);
    }
  }

  /**
   * Requests an Azure AD token to be issued on behalf of the app. The token is acquired from the cache
   * if it is not expired. Otherwise a request is sent to Azure AD to obtain a new token.
   * @param authTokenRequest A set of values that configure the token request.
   */
  export function getAuthToken(authTokenRequest: AuthTokenRequest): void {
    if (!authService) {
      throw Error('Standalone App not initialized');
    }

    let resource = appconfig.resource;
    if (authTokenRequest.resources) {
      resource = authTokenRequest.resources[0];
    }

    if (!resource) throw Error('Resource cannot be null');

    authService.acquireToken(resource).then((tokenResponse: IAuthenticationToken) => {
      if (tokenResponse.token) {
        authTokenRequest?.successCallback && authTokenRequest?.successCallback(tokenResponse.token);
      } else {
        authTokenRequest?.failureCallback && authTokenRequest?.failureCallback('token request failed.');
      }
    });
  }

  /**
   * @private
   * Hide from docs.
   * ------
   * Requests the decoded Azure AD user identity on behalf of the app.
   */
  export function getUser(userRequest: UserRequest): void {
    if (!authService) {
      throw Error('Standalone App not initialized');
    }

    if (__IS_E2E_TESTING__) {
      userRequest.successCallback?.({ oid: 'mockOid', upn: 'mockUpn' } as UserProfile);
      return;
    }

    if (!authService.isUserLoggedIn()) {
      userRequest.failureCallback && userRequest.failureCallback('User not logged in');
    } else {
      authService.getUser().then((authUser: IAuthenticationUser) => {
        userRequest.successCallback && userRequest.successCallback(authUser.profile as UserProfile);
      });
    }
  }

  /**
   * Notifies the frame that initiated this authentication request that the request was successful.
   * This function is usable only on the authentication window.
   * This call causes the authentication window to be closed.
   * @param _result Specifies a result for the authentication. If specified, the frame that initiated the authentication pop-up receives this value in its callback.
   * @param _callbackUrl Specifies the url to redirect back to if the client is Win32 Outlook.
   */
  export function notifySuccess(_result?: string, _callbackUrl?: string): void {}

  /**
   * Notifies the frame that initiated this authentication request that the request failed.
   * This function is usable only on the authentication window.
   * This call causes the authentication window to be closed.
   * @param result Specifies a result for the authentication. If specified, the frame that initiated the authentication pop-up receives this value in its callback.
   * @param _callbackUrl Specifies the url to redirect back to if the client is Win32 Outlook.
   */
  export function notifyFailure(_reason?: string, _callbackUrl?: string): void {}
}
